import { useCallback, useEffect, useState } from 'react';
import type { DeviceSummary } from '@mantelpiece/shared';
import { api, ApiError, type StoredSession } from '../lib/api';
import { go } from '../lib/route';
import { BillingBanner } from '../components/BillingBanner';
import { Shell } from '../components/Shell';
import { STANDING_PILL } from '../lib/standing';

interface Props {
  readonly session: StoredSession;
  readonly onSignOut: () => void;
  /** Makes this browser a screen showing that frame, and opens the viewer. */
  readonly onShowHere: (id: string) => void;
}

const ROLE_TEXT: Record<DeviceSummary['role'], string> = {
  owner: 'Yours',
  delegate: 'You help run this',
  guest: 'Shared with you',
};

export const Devices = ({ session, onSignOut, onShowHere }: Props) => {
  const [devices, setDevices] = useState<readonly DeviceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [claimKey, setClaimKey] = useState('');
  const [showClaim, setShowClaim] = useState(false);
  /** The last failure was the money, not the network: offer the way to fix it. */
  const [needsBilling, setNeedsBilling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDevices((await api.listDevices(session.token)).devices);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load your frames.');
    } finally {
      setLoading(false);
    }
  }, [session.token]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    setNeedsBilling(false);
    try {
      const created = await api.createDevice(session.token, label.trim() || 'Living room frame');
      setLabel('');
      go(`/d/${created.id}/screens`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not create that frame.');
      setNeedsBilling(caught instanceof ApiError && caught.code === 'payment_required');
    }
  };

  const claim = async () => {
    try {
      await api.claimLegacyDevice(session.token, claimKey.trim());
      setClaimKey('');
      setShowClaim(false);
      await load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not claim the existing frame.',
      );
    }
  };

  return (
    <Shell who={session.account.name} onSignOut={onSignOut}>
      <div className="stack-loose" style={{ marginTop: '2rem' }}>
        {error && (
          <p className="alert">
            {error}
            {needsBilling && (
              <>
                {' '}
                <button type="button" className="linky" onClick={() => go('/billing')}>
                  Go to billing
                </button>
              </>
            )}
          </p>
        )}

        <BillingBanner session={session} />

        <div>
          <div className="section-head">
            <p className="eyebrow">Your frames</p>
          </div>

          {loading ? (
            <p className="empty">Loading</p>
          ) : devices.length === 0 ? (
            <p className="empty">
              Nothing here yet. Create a frame below, then connect a TV to it — or wait for
              somebody to share theirs with you.
            </p>
          ) : (
            <ul className="ledger">
              {devices.map((device) => (
                <li key={device.id}>
                  <span className="ledger-main">
                    <span className="ledger-name">
                      <button type="button" className="linky" onClick={() => go(`/d/${device.id}`)}>
                        {device.label}
                      </button>
                      {device.standing !== 'live' && (
                        <span className="pill" data-tone={device.standing === 'grace' ? 'wait' : 'off'}>
                          {STANDING_PILL[device.standing]}
                        </span>
                      )}
                    </span>
                    <span className="ledger-meta">
                      {ROLE_TEXT[device.role]}
                      {device.role !== 'owner' ? ` · ${device.ownerName}'s` : ''} ·{' '}
                      {device.photoCount} photo{device.photoCount === 1 ? '' : 's'} ·{' '}
                      {device.screenCount} screen{device.screenCount === 1 ? '' : 's'}
                    </span>
                  </span>
                  <span className="row-tight">
                    {device.pendingCount > 0 && (
                      <span className="pill" data-tone="wait">
                        {device.pendingCount} waiting
                      </span>
                    )}
                    {/* Turns this browser into one of the frame's screens with
                        no code to type: the session already says who, and this
                        says which. The viewer picks both up from storage. */}
                    <button
                      type="button"
                      className="button button-quiet button-small"
                      onClick={() => onShowHere(device.id)}
                    >
                      Show here
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="section-head">
            <p className="eyebrow">Albums</p>
            <button type="button" className="button button-quiet button-small" onClick={() => go('/a')}>
              Open albums
            </button>
          </div>
          <p className="note">
            A set of photos you keep up to date, showing on as many frames as you like. Change the
            album and every frame follows.
          </p>
        </div>

        <div>
          <div className="section-head">
            <p className="eyebrow">Add a frame</p>
          </div>
          <p className="note" style={{ marginBottom: '1rem' }}>
            A frame is one photo library with its own settings and its own sharing. One frame can
            drive more than one television.
          </p>
          {/* The one decision people get backwards. A second screen showing the
              same pictures is a screen on an existing frame; a second frame is
              a second library, with its own sharing and its own queue. */}
          <p className="note" style={{ marginBottom: '1rem' }}>
            Only make a new one if it should show <em>different</em> photos. To put the same
            photos on another television, tablet or laptop, open the frame you already have and
            add a screen to it — they all stay in step.
          </p>
          <div className="row">
            <label className="field">
              <span>What to call it</span>
              <input
                className="input"
                value={label}
                maxLength={40}
                placeholder="Living room frame"
                onChange={(event) => setLabel(event.target.value)}
              />
            </label>
            <button type="button" className="button" onClick={() => void create()}>
              Create
            </button>
          </div>
        </div>

        {/* Only ever useful once, on a deployment that predates accounts. It is
            tucked away rather than removed so the upgrade path still exists. */}
        <div>
          <button type="button" className="linky" onClick={() => setShowClaim((now) => !now)}>
            {showClaim ? 'Never mind' : 'Claim a frame from before accounts existed'}
          </button>
          {showClaim && (
            <div className="stack" style={{ marginTop: '1rem' }}>
              <p className="note">
                If this site ran before it had accounts, its photos and TVs are waiting under an
                unowned frame. Paste the old admin key once to take ownership of it. You will never
                need the key again.
              </p>
              <div className="row">
                <label className="field">
                  <span>Old admin key</span>
                  <input
                    className="input"
                    type="password"
                    value={claimKey}
                    onChange={(event) => setClaimKey(event.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="button button-quiet"
                  disabled={claimKey.trim().length < 8}
                  onClick={() => void claim()}
                >
                  Claim
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
};
